import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon, TrendingDownIcon, TrendingUpIcon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { Tooltip, TooltipTrigger, TooltipPopup } from "@/components/ui/tooltip";
import { CountUp } from "@/components/ui/motion";
import { CornerDotFade } from "@/components/ui/decorative-patterns";
import { cn } from "@/lib/utils";
import { formatMetric, deltaPct, signedPct } from "@/lib/format";
import type { MetricUnit } from "@/lib/api/types";

export interface KpiTileProps {
  label: string;
  value: number | null | undefined;
  unit: MetricUnit;
  icon: IconSvgElement;
  /** Previous-period value, for the trend delta. */
  prev?: number | null;
  /** Benchmark target, rendered as a subtle "/ target" hint. */
  target?: number | null;
  /** Whether a higher value is better — drives the trend colour. null = neutral. */
  higherIsBetter?: boolean | null;
  /** Currency formatter for currency-unit values (from useMoney). */
  money?: (v: number | null | undefined) => string;
  /** Exactly what the figure counts. Shown on the tile behind an info icon so a
   *  number that looks "wrong" next to another tool can explain itself where it
   *  is being read, instead of in a separate document nobody opens. */
  description?: string;
  /** Shown under the value when the figure covers only part of the selection. */
  note?: string;
  /** The value is still being fetched (reach is asked of the platforms live). */
  pending?: boolean;
}

/** One metric card — ticketbalie house pattern in Prado green: a white value
 *  panel (count-up hero, corner dot-fade bottom-right) riding over a gray
 *  footer shelf with the uppercase label + icon chip. Stands alone — no
 *  hairline grid. Same props API, UI-only. */
export function KpiTile({ label, value, unit, icon: Icon, prev, target, higherIsBetter = true, money, description, note, pending }: KpiTileProps) {
  const delta = prev != null && value != null ? deltaPct(value, prev) : null;
  const trendUp = delta != null && delta > 0.0005;
  const trendDown = delta != null && delta < -0.0005;
  const good = higherIsBetter == null ? null : (trendUp ? higherIsBetter : trendDown ? !higherIsBetter : null);
  const fmt = (n: number) => formatMetric(n, unit, money);

  return (
    <div className="group block overflow-hidden rounded-xl border border-border/60 bg-[hsl(140_14%_97%)] transition-colors dark:bg-[hsl(140_8%_9%)]">
      {/* White content — the value is the hero. */}
      <div className="relative z-10 overflow-hidden rounded-b-xl border-b border-border/60 bg-card px-5 pb-5 pt-4">
        <CornerDotFade color="#10b981" />
        <div className="relative flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <p className="font-heading text-[1.7rem] font-semibold leading-none tracking-tight tabular-nums">
            {pending ? <span className="text-muted-foreground">…</span> : value == null ? formatMetric(value, unit, money) : <CountUp value={value} format={fmt} />}
          </p>
          {delta != null && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
                good === true && "bg-success/10 text-success-foreground",
                good === false && "bg-destructive/10 text-destructive-foreground",
                good === null && "bg-muted text-muted-foreground",
              )}
            >
              {trendUp ? <HugeiconsIcon icon={TrendingUpIcon} size={12} /> : trendDown ? <HugeiconsIcon icon={TrendingDownIcon} size={12} /> : null}
              {signedPct(delta)}
            </span>
          )}
        </div>
        {target != null && (
          <p className="relative mt-2 truncate text-[11px] leading-none text-muted-foreground/70">
            doel {formatMetric(target, unit, money)}
          </p>
        )}
        {note && !pending && (
          <p className="relative mt-2 text-[11px] leading-snug text-amber-700 dark:text-amber-400">{note}</p>
        )}
      </div>

      {/* Gray shelf — label + hint + icon chip. */}
      <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-2.5">
        <div className="flex min-w-0 items-center gap-1">
          <p className="min-w-0 truncate text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          {description && (
            <Tooltip>
              <TooltipTrigger
                delay={0}
                closeOnClick={false}
                aria-label={`Wat telt ${label} mee?`}
                className="shrink-0 cursor-help rounded p-1 text-muted-foreground/60 transition-colors hover:text-foreground"
              >
                <HugeiconsIcon icon={InformationCircleIcon} size={14} />
              </TooltipTrigger>
              <TooltipPopup className="max-w-xs text-xs leading-relaxed">{description}</TooltipPopup>
            </Tooltip>
          )}
        </div>
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
          <HugeiconsIcon icon={Icon} size={20} className="h-[18px] w-[18px]" />
        </span>
      </div>
    </div>
  );
}
